import React, { useState } from "react";
import NoSsr from "@mui/material/NoSsr";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import useFormValidation from "./FormValidation";
import validateAuth, { Errors } from "./ValidateAuth";
import "./style.scss";
import Alert from "@mui/material/Alert";
import { useSelector } from "react-redux";
import { RootStateType } from "@Interfaces";
import {
    TextField,
    InputAdornment,
    IconButton,
    MenuItem,
    Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";

interface Option {
    value: string;
    label: string;
}

const getEntityTypeList = (t: (key: string) => string): Option[] => {
    const options: Option[] = [
        { value: "event_company", label: "signup.options.event_company" },
        { value: "a_store", label: "signup.options.a_store" },
        { value: "chain_store", label: "signup.options.chain_store" },
        { value: "delivery_company", label: "signup.options.delivery_company" },
        { value: "ngo", label: "signup.options.ngo" },
        { value: "university", label: "signup.options.university" },
        {
            value: "government_agency",
            label: "signup.options.government_agency",
        },
    ];

    return options.map((option) => ({
        value: option.value,
        label: t(option.label), // Usar `t()` para traducir la etiqueta
    }));
};

const INITIAL_STATE = {
    username: "",
    password: "",
    confirm_password: "",
    email: "",
    entityType: "",
    url: "",
};

export const SignupForm: React.FC<any> = (props) => {
    const { t } = useTranslation();
    const entityTypeList = getEntityTypeList(t);
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = () => setShowPassword(!showPassword);
    const isAccountTaken = useSelector(
        (state: RootStateType) => state.auth.isAccountTaken
    );

    const {
        handleSubmit,
        handleChange,
        handleUsernameEmail,
        values,
        errors,
        isSubmitting,
    } = useFormValidation(INITIAL_STATE, validateAuth, props);

    const getErrorMessage = (field: keyof Errors): string => {
        if (errors[field]) {
            const errorType = errors[field];
            return t(`signup.errors.${field}.${errorType}`);
        }
        return "";
    };

    return (
        <NoSsr>
            <Grid container spacing={0}>
                <Grid item xs={12}>
                    <Container component="main" maxWidth="xs">
                        <Box
                            sx={{
                                marginTop: "35px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: "60px",
                                    height: "60px",
                                    backgroundColor: "transparent",
                                }}
                                src="/images/edit-image.png"
                            />
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{ color: "#334768" }}
                            >
                                {t("signup.title")}
                            </Typography>

                            <form
                                style={{
                                    width: "100%",
                                    marginTop: "24px",
                                }}
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <Grid container spacing={2}>
                                    {isAccountTaken && (
                                        <Grid item xs={12} sm={12}>
                                            <Alert
                                                severity="error"
                                                variant="filled"
                                            >
                                                {t("signup.account_taken")}
                                            </Alert>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            autoComplete="username"
                                            name="username"
                                            variant="outlined"
                                            required
                                            fullWidth
                                            id="username"
                                            label={t("signup.username")}
                                            autoFocus
                                            onChange={handleChange}
                                            onFocus={handleUsernameEmail}
                                            value={values.username}
                                            error={!!errors.username}
                                            helperText={
                                                !!errors.username &&
                                                getErrorMessage("username")
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            required
                                            fullWidth
                                            id="email"
                                            label={t("signup.email")}
                                            name="email"
                                            autoComplete="email"
                                            onChange={handleChange}
                                            onFocus={handleUsernameEmail}
                                            value={values.email}
                                            error={!!errors.email}
                                            helperText={
                                                !!errors.email &&
                                                getErrorMessage("email")
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            required
                                            fullWidth
                                            name="password"
                                            label={t("signup.password")}
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            id="password"
                                            autoComplete="current-password"
                                            onChange={handleChange}
                                            value={values.password}
                                            error={!!errors.password}
                                            helperText={
                                                !!errors.password &&
                                                getErrorMessage("password")
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={
                                                                handleClickShowPassword
                                                            }
                                                            onMouseDown={
                                                                handleMouseDownPassword
                                                            }
                                                            size="large"
                                                        >
                                                            {showPassword ? (
                                                                <Visibility />
                                                            ) : (
                                                                <VisibilityOff />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            required
                                            fullWidth
                                            name="confirm_password"
                                            label={t("signup.confirm_password")}
                                            type="password"
                                            id="confirm_password"
                                            onChange={handleChange}
                                            value={values.confirm_password}
                                            error={!!errors.confirm_password}
                                            helperText={
                                                !!errors.confirm_password &&
                                                getErrorMessage(
                                                    "confirm_password"
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            variant="outlined"
                                            value={values.entityType}
                                            fullWidth
                                            name="entityType"
                                            label={t("signup.entity_type")}
                                            id="entityType"
                                            onChange={handleChange}
                                            error={!!errors.entityType}
                                            helperText={
                                                !!errors.entityType &&
                                                getErrorMessage("entityType")
                                            }
                                        >
                                            {entityTypeList.map((option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            autoComplete="url"
                                            name="url"
                                            variant="outlined"
                                            fullWidth
                                            id="url"
                                            label={t("signup.url")}
                                            onChange={handleChange}
                                            value={values.url}
                                            error={!!errors.url}
                                            helperText={
                                                !!errors.url
                                                    ? getErrorMessage("url")
                                                    : t("signup.optional")
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                <Button
                                    disabled={isSubmitting}
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        my: 3,
                                        mb: 2,
                                        backgroundColor: "#1fc2ff",
                                        borderRadius: "2em",
                                        border: "none",
                                        color: "#fff",
                                        outline: "none",
                                    }}
                                >
                                    {t("signup.join_now")}
                                </Button>
                            </form>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </NoSsr>
    );
};
